import React, { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import styles from "./ComplianceChart.module.css";
import {
	CustomButton,
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
} from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import { setComplianceFilters } from "../../../slice/project-sheet-slice";

interface ComplianceData {
	compliant: number;
	nonCompliant: number;
	pending: number;
}

type Segment = "compliant" | "nonCompliant" | "pending";

interface DonutSegmentProps {
	cx: number;
	cy: number;
	r: number;
	strokeWidth: number;
	percentage: number;
	offset: number;
	className: string;
	dimmed: boolean;
	labelKey: string;
	onClick: () => void;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
	const rad = ((angleDeg - 90) * Math.PI) / 180;
	return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
	const start = polarToCartesian(cx, cy, r, endAngle);
	const end = polarToCartesian(cx, cy, r, startAngle);
	const largeArc = endAngle - startAngle <= 180 ? "0" : "1";
	return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function DonutSegment({
	cx,
	cy,
	r,
	strokeWidth,
	percentage = 0,
	labelKey,
	offset,
	className,
	dimmed,
	onClick,
}: Readonly<DonutSegmentProps>) {
	const isFullCircle = percentage >= 99.99;
	if (isFullCircle) {
		return (
			<CustomTooltip
				title={translate(`dashboards.project-sheet.compliance-status.filter.${labelKey}.tooltip`, {
					percentage: percentage.toFixed(2),
				})}
			>
				<circle
					className={`${styles.geoShape} ${className}`}
					cx={cx}
					cy={cy}
					data-dimmed={dimmed}
					fill="none"
					opacity={dimmed ? 0.18 : 1}
					r={r}
					strokeWidth={strokeWidth}
					onClick={onClick}
				/>
			</CustomTooltip>
		);
	}
	const gap = 0;
	const startAngle = (offset / 100) * 360;
	const endAngle = ((offset + percentage) / 100) * 360;
	const d = describeArc(cx, cy, r, startAngle + gap / 2, endAngle - gap / 2);
	return (
		<CustomTooltip
			title={translate(`dashboards.project-sheet.compliance-status.filter.${labelKey}.tooltip`, {
				percentage: percentage.toFixed(2),
			})}
		>
			<path
				className={`${styles.geoShape} ${className}`}
				d={d}
				data-dimmed={dimmed}
				fill="none"
				opacity={dimmed ? 0.18 : 1}
				strokeWidth={strokeWidth}
				onClick={onClick}
			/>
		</CustomTooltip>
	);
}

export default function ComplianceChart({
	data,
	isLoading,
}: {
	readonly data: ComplianceData;
	readonly isLoading: boolean;
}) {
	const [selected, setSelected] = useState<Set<Segment>>(new Set());
	const containerRef = useRef<HTMLDivElement>(null);
	const dispatch = useDispatch();
	const total = data.compliant + data.nonCompliant + data.pending;
	const segments: { key: Segment; value: number; className: string; labelKey: string }[] = useMemo(
		() => [
			{
				key: "compliant",
				value: data.compliant,
				className: styles.strokeStatus__green,
				labelKey: "compliant",
			},
			{
				key: "pending",
				value: data.pending,
				className: styles.strokeStatus__orange,
				labelKey: "pending",
			},
			{
				key: "nonCompliant",
				value: data.nonCompliant,
				className: styles.strokeStatus__red,
				labelKey: "non-compliant",
			},
		],
		[data]
	);
	let offsetAcc = 0;
	const segmentsWithOffset = segments.map((s) => {
		const pct = (s.value / total) * 100;
		const result = { ...s, pct, offset: offsetAcc };
		offsetAcc += pct;
		return result;
	});

	const cx = 115;
	const cy = 75;
	const r = 55;
	const strokeWidth = 18;
	const isEmpty = total === 0;

	const presentSegments = useMemo(() => segments.filter((s) => s.value > 0).map((s) => s.key), [segments]);
	const toggleSegment = useCallback(
		(segment: Segment) => {
			setSelected((prev) => {
				const next = new Set(prev);
				if (next.has(segment)) {
					next.delete(segment);
				} else {
					next.add(segment);
				}
				if (next.size === presentSegments.length) {
					return new Set();
				}
				return next;
			});
		},
		[presentSegments]
	);
	useEffect(() => {
		dispatch(setComplianceFilters(Array.from(selected).map((s) => (s === "nonCompliant" ? "non_compliant" : s))));
	}, [dispatch, selected]);
	const clickHandlers: Record<Segment, () => void> = {
		compliant: () => toggleSegment("compliant"),
		nonCompliant: () => toggleSegment("nonCompliant"),
		pending: () => toggleSegment("pending"),
	};
	const handleClearSelection = useCallback(() => {
		setSelected(new Set());
	}, []);
	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.skeletonTitle} />
				<div className={styles.chartWrapper}>
					<div className={styles.skeletonDonut} />
				</div>
			</div>
		);
	}
	return (
		<div ref={containerRef} className={styles.container}>
			<div className={styles.title}>
				<span className={styles.titleText}>
					{translate("dashboards.project-sheet.compliance-status.filter.title")}
				</span>
				<CustomTooltip title={translate("dashboards.project-sheet.compliance-status.filter.information")}>
					<IconComponent color="var(--grey-500)" icon={icon.faInfoCircle} />
				</CustomTooltip>
			</div>
			<div className={styles.chartWrapper}>
				<svg className={styles.svg} height={cy * 2} viewBox={`0 0 ${cx * 2} ${cy * 2}`} width={cx * 2}>
					<circle cx={cx} cy={cy} fill="none" r={r} stroke="var(--color-white)" strokeWidth={strokeWidth} />
					{isEmpty ? (
						<CustomTooltip title={translate("dashboards.project-sheet.compliance-status.filter.empty")}>
							<circle
								cx={cx}
								cy={cy}
								fill="none"
								r={r}
								stroke="var(--grey-400)"
								strokeWidth={strokeWidth}
							/>
						</CustomTooltip>
					) : (
						segmentsWithOffset
							.filter((s) => s.pct > 0)
							.map((s) => (
								<DonutSegment
									key={s.key}
									className={s.className}
									cx={cx}
									cy={cy}
									dimmed={selected.size > 0 && !selected.has(s.key)}
									labelKey={s.labelKey}
									offset={s.offset}
									percentage={s.pct}
									r={r}
									strokeWidth={strokeWidth}
									onClick={clickHandlers[s.key]}
								/>
							))
					)}
				</svg>
				<div className={styles.centerLabel}>
					<span className={styles.centerLabelText}>{isEmpty ? "N/A" : `${data.compliant}%`}</span>
				</div>
			</div>
			{selected.size > 0 && (
				<div className={styles.selectionPill}>
					<CustomButton
						color="primary"
						startIcon={icon.faChartPie}
						variant="outlined"
						onClick={handleClearSelection}
					>
						{translate("dashboards.project-sheet.compliance-status.compliance")}
					</CustomButton>
					<CustomIconButton
						color="primary"
						icon={icon.faXmark}
						size="sm"
						variant="outlined"
						onClick={handleClearSelection}
					/>
				</div>
			)}
		</div>
	);
}
